//app/(app)/(root)/_layout.web.tsx
import { Link, Redirect, Tabs } from "expo-router";
import { Platform, StyleSheet, useWindowDimensions, View } from "react-native";
import { HapticTab } from "@/components/HapticTab";
import { IconSymbol, IconSymbolName } from "@/components/ui/IconSymbol";
import { Image, type ImageSource } from "expo-image";
import TabBarBackground from "@/components/ui/TabBarBackground";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import CustomTabBarWrapper from "@/components/CustomTabBar";
import { WebTab } from "@/components/WebTab";
import { ThemedText } from "@/components/ThemedText";
import useNavigationGuard from "@/hooks/useNavigationGuard";
import useRealtime from "@/hooks/useRealtime";
import { ENTITY_ICONS, EntityIconKey } from "@/constants/icons";
import { useContext, useState } from "react";
import { Button } from "@/components/ui/Button";
import Animated, { useAnimatedRef, useAnimatedStyle, useSharedValue } from "react-native-reanimated";
import { useStorage } from "@/hooks/useStorage";
import { useBadge } from "@/hooks/useBadge";
import { useSMS } from "@/hooks/useSMS";
import { CALENDAR_DAYS_VIEW_KEY, DEFAULT_DAYS_VIEW } from "@/lib/calendar";
import {
  Gesture,
  GestureDetector,
  GestureStateChangeEvent,
  PanGestureHandlerEventPayload,
} from "react-native-gesture-handler";

import DrawerBody from "@/components/DrawerBody";
import { hasAdminPortal } from "@/lib/permissions";
import { RootLayoutContext } from "@/app/(app)/_layout";
import * as Device from "expo-device";
import { Drawer } from "expo-router/drawer";
import Profile from "@/components/Header/ProfileButton";

export default function RootLayout() {
  const { session, loading, displayNames, userRoles } = useAuth();
  const storage = useStorage();
  const {
    hasChatAccess: hasPhoneNumbers,
    hasProjectsAccess,
    hasUsersAccess,
    hasClientsAccess,
    hasContactsAccess,
    hasInvoicesAccess,
    hasEstimatesAccess,
    hasPayrollAccess,
    hasExpensesAccess,
    hasIncidentsAccess,
    hasInvestigationsAccess,
    hasCoursesAccess,
    hasApplicationsAccess,
  } = usePermissions();

  const badge = useBadge();
  const { drawerPinned, tabPress, tabLongPress } = useNavigationGuard();
  const { colors, toggleTheme } = useTheme();
  const { timeCardsIssueCount } = useRealtime();
  const { totalUnread } = useSMS();
  const chatBadge = totalUnread > 0 ? totalUnread : undefined;
  const timecardBadge = timeCardsIssueCount !== 0 ? timeCardsIssueCount : undefined;

  const [logo] = useState<ImageSource>(require("@/assets/images/rockforce-logo-rf-yellow.svg"));
  const rootLayoutCtx = useContext(RootLayoutContext);
  const { width } = useWindowDimensions();

  // Check if user has admin portal access

  // const hasAdminPortal = userRoles?.some((role) =>
  //   role.permissions?.some((permission) => permission.admin_portal === true),
  // );
  //
  // if (!hasAdminPortal) {
  //   return <Redirect href="/(app)/(crew)/home" />;
  // }

  const listeners = () => ({
    tabPress: tabPress,
    tabLongPress: tabLongPress,
  });
  type TabOptionsObject =
    Exclude<React.ComponentProps<typeof Tabs.Screen>["options"], undefined> extends (...args: any) => infer R
      ? R
      : Exclude<React.ComponentProps<typeof Tabs.Screen>["options"], undefined>;

  type IconFnArgs = { focused: boolean; color: string; size: number };

  const makeTabOpts = (
    title: string,
    icon: IconSymbolName,
    canSee = true,
    tabBarBadge?: string | number,
  ): TabOptionsObject => ({
    ...(canSee ? {} : { href: null }),
    title,
    tabBarBadge,
    tabBarIcon: ({ color }: IconFnArgs): React.ReactNode => <IconSymbol size={24} name={icon} color={color} />,
  });

  const getIcon = (key: EntityIconKey): IconSymbolName => ENTITY_ICONS[key];

  const savedSize = storage.getNumber(`shared-flyout`) ?? 400;
  const savedDaysView = storage.getNumber(CALENDAR_DAYS_VIEW_KEY) ?? DEFAULT_DAYS_VIEW;

  const sharedSize = useSharedValue<number>(isNaN(savedSize) ? 50 : savedSize);
  const pinnedRef = useAnimatedRef();

  const onGestureEnd = (_event?: GestureStateChangeEvent<PanGestureHandlerEventPayload>) => {
    "worklet";
    storage.saveFast(`shared-flyout`, sharedSize.get().toString());
  };

  const leftInset = rootLayoutCtx?.tabsCollapsed ? 67 : 300;
  const parentWidth = width - leftInset;

  const dragX = Gesture.Pan()
    .onChange((e) => {
      "worklet";
      if (!pinnedRef.current) return;
      const deltaPx = e.changeX;
      const deltaPercent = (deltaPx / parentWidth) * 100;
      const nextValue = sharedSize.get() - deltaPercent;
      if (nextValue < 25) return;
      if (nextValue > 99) return;
      sharedSize.set(nextValue);
    })
    .onEnd(onGestureEnd);

  const gesture = dragX;

  const animatedStyle = useAnimatedStyle(() => {
    const percentOfAvailable = sharedSize.get();
    const pixelWidth = (percentOfAvailable / 100) * parentWidth;
    return {
      width: pixelWidth,
    };
  });

  const TabButton = Platform.OS === "web" ? WebTab : HapticTab;
  const TabsNode = (
    <Tabs
      detachInactiveScreens={true}
      tabBar={(props) => <CustomTabBarWrapper {...props} />}
      backBehavior="order"
      screenOptions={{
        freezeOnBlur: true,
        tabBarHideOnKeyboard: true,
        sceneStyle: {
          backgroundColor: colors.backgroundSecondary,
          borderColor: colors.border,
        },
        tabBarLabelStyle: Platform.select({
          web: {
            fontSize: 14,
            marginLeft: 8,
          },
        }),
        tabBarPosition: Platform.select({
          default: "bottom",
          web: "left",
        }),
        tabBarLabelPosition: "beside-icon",
        tabBarActiveBackgroundColor: colors.primary,
        tabBarActiveTintColor: "white",
        // tabBarLabel: ({ focused, color, children }) => (
        //   <ThemedText
        //     style={{
        //       color,
        //       fontWeight: focused ? "bold" : "normal",
        //       fontSize: 14,
        //       // textTransform: "capitalize",
        //     }}
        //   >
        //     {children}
        //   </ThemedText>
        // ),
        // tabBarLabel: ({ focused, color, children }) => (
        //   <ThemedText
        //     style={{
        //       color,
        //       fontWeight: focused ? "bold" : "normal",
        //       fontSize: 14,
        //       // textTransform: "capitalize",
        //     }}
        //   >
        //     {children}
        //   </ThemedText>
        // ),
        headerShown: false,
        headerShadowVisible: false,
        tabBarBadgeStyle: {
          maxWidth: 43,
          fontSize: 12,
          top: -6,
          right: -13,
        },
        tabBarButton: TabButton,
        tabBarBackground: TabBarBackground,
        tabBarStyle: Platform.select({
          ios: {
            position: "absolute",
            borderColor: colors.border,
          },
          web: {
            backgroundColor: colors.backgroundSecondary,
            minWidth: 20,
            borderRightWidth: 0,
            borderColor: colors.border,
          },
          default: {
            backgroundColor: colors.backgroundSecondary,
            borderColor: colors.border,
          },
        }),
      }}
    >
      <Tabs.Screen name="index" options={makeTabOpts("Dashboard", getIcon("dashboard"), true)} listeners={listeners} />
      <Tabs.Screen
        name="(projects)"
        options={makeTabOpts(
          displayNames?.projects?.plural ? displayNames.projects.plural : "Projects",
          getIcon("projects"),
          hasProjectsAccess,
        )}
        listeners={listeners}
      />
      <Tabs.Screen
        name="(shifts)"
        options={makeTabOpts(
          displayNames?.shifts?.plural ? displayNames.shifts.plural : "Schedules",
          getIcon("shifts"),
          true,
          badge.shifts,
        )}
        initialParams={{ daysView: savedDaysView.toString() }}
        listeners={listeners}
      />
      <Tabs.Screen
        name="(chat)"
        options={makeTabOpts(displayNames?.chat?.plural || "Chats", getIcon("chat"), hasPhoneNumbers, chatBadge)}
        listeners={listeners}
      />
      <Tabs.Screen
        name="(time_cards)"
        options={makeTabOpts(
          displayNames?.time_cards?.plural || "Time Cards",
          getIcon("time_cards"),
          true,
          timecardBadge,
        )}
        listeners={listeners}
      />
      <Tabs.Screen
        name="(expenses)"
        options={makeTabOpts(
          displayNames?.expenses?.plural || "Expenses",
          getIcon("expenses"),
          hasExpensesAccess,
          badge.expenses,
        )}
        listeners={listeners}
      />
      <Tabs.Screen
        name="(invoices)"
        options={makeTabOpts(
          displayNames?.invoices?.plural || "Invoices",
          getIcon("invoices"),
          hasInvoicesAccess,
          badge.invoices,
        )}
        listeners={listeners}
      />
      <Tabs.Screen
        name="(payroll)"
        options={makeTabOpts(displayNames?.payroll?.plural || "Payroll", getIcon("payroll"), hasPayrollAccess)}
        listeners={listeners}
      />
      <Tabs.Screen
        name="(users)"
        options={makeTabOpts(displayNames?.users?.plural || "Users", getIcon("users"), hasUsersAccess)}
        listeners={listeners}
      />
      <Tabs.Screen
        name="(contacts)"
        options={{
          ...makeTabOpts(displayNames?.contacts?.plural || "Contacts", getIcon("contacts"), hasContactsAccess),
        }}
        listeners={listeners}
      />
      <Tabs.Screen
        name="(clients)"
        options={makeTabOpts(displayNames?.clients?.plural || "Clients", getIcon("clients"), hasClientsAccess)}
        listeners={listeners}
      />

      <Tabs.Screen name="crew" options={makeTabOpts("Crew Portal", getIcon("crew"), true)} listeners={listeners} />
      <Tabs.Screen
        name="(applications)"
        options={makeTabOpts(
          displayNames?.applications?.plural || "Applications",
          getIcon("applications"),
          hasApplicationsAccess,
        )}
        listeners={listeners}
      />
      <Tabs.Screen
        name="(incidents)"
        options={makeTabOpts(
          displayNames?.incidents?.plural || "Incidents",
          getIcon("incidents"),
          hasIncidentsAccess,
          badge.incidents,
        )}
        listeners={listeners}
      />
      {/* Hide Investigations, estimates, courses for now */}
      <Tabs.Screen
        name="(investigations)"
        // options={makeTabOpts(
        //   displayNames?.investigations?.plural || "Investigations",
        //   getIcon("investigations"),
        //   hasInvestigationsAccess,
        // )
        options={{
          title: displayNames?.investigations?.plural || "Investigations",
          href: null,
        }}
        listeners={listeners}
      />
      <Tabs.Screen
        name="(estimates)"
        options={{
          title: displayNames?.estimates?.plural || "Estimates",
          href: null,
        }}
        // options={makeTabOpts(displayNames?.estimates?.plural || "Estimates", getIcon("estimates"), hasEstimatesAccess)}
        listeners={listeners}
      />
      <Tabs.Screen
        name="(courses)"
        options={{
          title: displayNames?.courses?.plural || "Courses",
          href: null,
        }}
        // options={makeTabOpts(displayNames?.courses?.plural || "Courses", getIcon("courses"), hasCoursesAccess)}
        listeners={listeners}
      />
    </Tabs>
  );

  if (!session && !loading) {
    return <Redirect href="/sign-in" />;
  }

  if (!loading && !hasAdminPortal(userRoles)) {
    return <Redirect href="/(app)/(crew)/home" />;
  }

  const mobileLayout = Platform.OS !== "web" || Device.deviceType === Device.DeviceType.PHONE;

  // native and mobile share layout
  if (mobileLayout) {
    return (
      <Drawer
        backBehavior="order"
        initialRouteName="(users)"
        screenOptions={{
          drawerStyle: { backgroundColor: colors.backgroundSecondary },
          drawerLabelStyle: { color: colors.text },
          sceneStyle: { backgroundColor: colors.backgroundSecondary },
          drawerActiveBackgroundColor: colors.primary,
          headerStyle: {
            backgroundColor: colors.background,
            borderBottomWidth: StyleSheet.hairlineWidth,
            borderBottomColor: colors.border,
          },
          headerShadowVisible: false,
          drawerActiveTintColor: "white",
          headerShown: true,
          headerTintColor: colors.text,
          headerRight: () => (
            <View
              style={{
                flex: 1,
                gap: 8,
                flexDirection: "row",
                justifyContent: "space-between",
                alignContent: "center",
                alignItems: "center",
              }}
            >
              <View style={{ flexDirection: "row", gap: 8, alignItems: "center" }}>
                <Link asChild href={"/"}>
                  <Image source={[logo, { width: 100 }]} style={{ height: 34, width: 34, marginLeft: 12 }} />
                </Link>
                <Link asChild href={"/"}>
                  <ThemedText style={{ fontWeight: 700, fontSize: 18 }}>NEXUS</ThemedText>
                </Link>
              </View>
              <View>
                <View style={[styles.headerRight]}>
                  <Button
                    style={{ height: 36, width: 36, marginRight: 8 }}
                    iconSize={22}
                    onPress={() => toggleTheme()}
                    icon="theme-light-dark"
                  />
                  {session && !mobileLayout && <Profile />}
                </View>
              </View>
            </View>
          ),
        }}
      >
        <Drawer.Screen name="index" options={{ title: "Dashboard", drawerLabel: "Dashboard", headerShown: true }} />
        <Drawer.Screen
          name="(projects)"
          options={{
            title: displayNames?.projects?.plural || "Projects",
            drawerItemStyle: hasProjectsAccess ? undefined : { display: "none" },
            drawerLabel: hasProjectsAccess ? displayNames?.projects?.plural || "Projects" : undefined,
          }}
        />
        <Drawer.Screen
          name="(shifts)"
          options={{
            title: displayNames?.shifts?.plural || "Shifts",
            drawerLabel: displayNames?.shifts?.plural || "Shifts",
          }}
          initialParams={{ daysView: savedDaysView.toString() }}
        />
        <Drawer.Screen
          name="(chat)"
          options={{
            title: displayNames?.chat?.plural || "Chats",
            drawerItemStyle: hasPhoneNumbers ? undefined : { display: "none" },
            drawerLabel: hasPhoneNumbers ? displayNames?.chat?.plural || "Chats" : undefined,
          }}
        />
        <Drawer.Screen
          name="(time_cards)"
          options={{
            title: displayNames?.time_cards?.plural || "Time Cards",
            drawerLabel: displayNames?.time_cards?.plural || "Time Cards",
          }}
        />
        <Drawer.Screen
          name="(expenses)"
          options={{
            title: displayNames?.expenses?.plural || "Expenses",
            drawerItemStyle: hasExpensesAccess ? undefined : { display: "none" },
            drawerLabel: hasExpensesAccess ? displayNames?.expenses?.plural || "Expenses" : undefined,
          }}
        />
        <Drawer.Screen
          name="(invoices)"
          options={{
            title: displayNames?.invoices?.plural || "Invoices",
            drawerItemStyle: hasInvoicesAccess ? undefined : { display: "none" },
            drawerLabel: hasInvoicesAccess ? displayNames?.invoices?.plural || "Invoices" : undefined,
          }}
        />
        <Drawer.Screen
          name="(payroll)"
          options={{
            title: displayNames?.payroll?.plural || "Payroll",
            drawerItemStyle: hasPayrollAccess ? undefined : { display: "none" },
            drawerLabel: hasPayrollAccess ? displayNames?.payroll?.plural || "Payroll" : undefined,
          }}
        />
        <Drawer.Screen
          name="(users)"
          options={{
            title: displayNames?.users?.plural || "Users",
            drawerItemStyle: hasUsersAccess ? undefined : { display: "none" },
            drawerLabel: hasUsersAccess ? displayNames?.users?.plural || "Users" : undefined,
          }}
        />
        <Drawer.Screen
          name="(contacts)"
          options={{
            title: displayNames?.contacts?.plural || "Contacts",
            drawerItemStyle: hasContactsAccess ? undefined : { display: "none" },
            drawerLabel: hasContactsAccess ? displayNames?.contacts?.plural || "Contacts" : undefined,
          }}
        />
        <Drawer.Screen
          name="(clients)"
          options={{
            title: displayNames?.clients?.plural || "Clients",
            drawerItemStyle: hasClientsAccess ? undefined : { display: "none" },
            drawerLabel: hasClientsAccess ? displayNames?.clients?.plural || "Clients" : undefined,
          }}
        />

        <Drawer.Screen name="crew" options={{ drawerLabel: "Crew" }} />
        <Drawer.Screen
          name="(applications)"
          options={{
            title: displayNames?.applications?.plural || "Applications",
            drawerItemStyle: hasApplicationsAccess ? undefined : { display: "none" },
            drawerLabel: hasApplicationsAccess ? displayNames?.applications?.plural || "Applications" : undefined,
          }}
        />
        <Drawer.Screen
          name="(incidents)"
          options={{
            title: displayNames?.incidents?.plural || "Incidents",
            drawerItemStyle: hasIncidentsAccess ? undefined : { display: "none" },
            drawerLabel: hasIncidentsAccess ? displayNames?.incidents?.plural || "Incidents" : undefined,
          }}
        />
        <Drawer.Screen
          name="(investigations)"
          options={{
            title: displayNames?.investigations?.plural || "Investigations",
            drawerItemStyle: hasInvestigationsAccess ? undefined : { display: "none" },
            // drawerLabel: hasInvestigationsAccess ? displayNames?.investigations?.plural || "Investigations" : undefined,
            drawerLabel: undefined,
          }}
        />
        <Drawer.Screen
          name="(estimates)"
          options={{
            title: displayNames?.estimates?.plural || "Estimates",
            drawerItemStyle: hasEstimatesAccess ? undefined : { display: "none" },
            // drawerLabel: hasEstimatesAccess ? displayNames?.estimates?.plural || "Estimates" : undefined,
            drawerLabel: undefined,
          }}
        />
        <Drawer.Screen
          name="(courses)"
          options={{
            title: displayNames?.courses?.plural || "Courses",
            drawerItemStyle: hasCoursesAccess ? undefined : { display: "none" },
            // drawerLabel: hasCoursesAccess ? displayNames?.courses?.plural || "Courses" : undefined,
            drawerLabel: undefined,
          }}
        />
      </Drawer>
    );
  }
  // web desktop layout
  return (
    <View style={{ flexDirection: "row", height: "100%" }}>
      {TabsNode}
      {drawerPinned ? (
        <>
          <GestureDetector gesture={gesture}>
            <Button
              style={[
                { borderWidth: 0, borderRadius: 0 },
                //@ts-ignore
                { cursor: "col-resize" },
                { minWidth: 4, minHeight: "100%", backgroundColor: colors.border },
              ]}
            />
          </GestureDetector>
          <Animated.View ref={pinnedRef} style={animatedStyle}>
            <DrawerBody />
          </Animated.View>
        </>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  headerRight: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "flex-end",
  },
});
